import React from 'react';
import { Helmet } from 'react-helmet-async';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Schema Markup Component - Generates JSON-LD for all page types
export const SchemaMarkup = ({ type, data }) => {
  const generateSchema = () => {
    const baseOrganization = {
      "@type": "Organization",
      "name": "Arreglatelo",
      "url": "https://rma.arreglatelo.es",
      "logo": "https://rma.arreglatelo.es/logo.png",
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+34-624-47-32-93",
        "contactType": "customer service",
        "availableLanguage": ["Spanish", "English", "French", "Italian", "German"]
      },
      "sameAs": [
        "https://www.facebook.com/arreglatelo",
        "https://www.instagram.com/arreglatelo",
        "https://twitter.com/arreglatelo"
      ]
    };

    switch (type) {
      case 'LocalBusiness':
        return {
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": "https://rma.arreglatelo.es/#organization",
          "name": data.name || "Arreglatelo - Reparación de Electrónica",
          "image": data.image || "https://rma.arreglatelo.es/logo.png",
          "url": data.url || "https://rma.arreglatelo.es",
          "telephone": data.phone || "+34-624-47-32-93",
          "email": data.email || "info@arreglatelo.es",
          "priceRange": "€€",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": data.street || "Calle Principal 123",
            "addressLocality": data.city || "Madrid",
            "addressRegion": data.region || "Madrid",
            "postalCode": data.postalCode || "28001",
            "addressCountry": "ES"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": data.lat || 40.4168,
            "longitude": data.lng || -3.7038
          },
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              "opens": "09:00",
              "closes": "19:00"
            },
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": "Saturday",
              "opens": "10:00",
              "closes": "14:00"
            }
          ],
          "aggregateRating": data.rating ? {
            "@type": "AggregateRating",
            "ratingValue": data.rating.value || "4.8",
            "reviewCount": data.rating.count || "256"
          } : undefined,
          "areaServed": {
            "@type": "Country",
            "name": "Spain"
          },
          "serviceArea": {
            "@type": "GeoCircle",
            "geoMidpoint": {
              "@type": "GeoCoordinates",
              "latitude": 40.4168,
              "longitude": -3.7038
            },
            "geoRadius": "500000"
          }
        };

      case 'Service':
        return {
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": data.serviceType || "Electronics Repair",
          "name": data.name,
          "description": data.description,
          "provider": baseOrganization,
          "areaServed": {
            "@type": "Country",
            "name": "Spain"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Servicios de Reparación",
            "itemListElement": data.services?.map((service, idx) => ({
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": service.name,
                "description": service.description
              },
              "priceSpecification": {
                "@type": "PriceSpecification",
                "price": service.price,
                "priceCurrency": "EUR",
                "minPrice": service.minPrice,
                "maxPrice": service.maxPrice
              }
            })) || []
          }
        };

      case 'Product':
        return {
          "@context": "https://schema.org",
          "@type": "Product",
          "name": data.name,
          "description": data.description,
          "image": data.image,
          "brand": {
            "@type": "Brand",
            "name": data.brand
          },
          "category": data.category,
          "offers": {
            "@type": "AggregateOffer",
            "priceCurrency": "EUR",
            "lowPrice": data.lowPrice,
            "highPrice": data.highPrice,
            "offerCount": data.offerCount || 1,
            "availability": "https://schema.org/InStock"
          },
          "aggregateRating": data.rating ? {
            "@type": "AggregateRating",
            "ratingValue": data.rating.value,
            "reviewCount": data.rating.count,
            "bestRating": "5",
            "worstRating": "1"
          } : undefined
        };

      case 'FAQ':
        return {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": data.faqs?.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer
            }
          })) || []
        };

      case 'HowTo':
        return {
          "@context": "https://schema.org",
          "@type": "HowTo",
          "name": data.name,
          "description": data.description,
          "image": data.image,
          "totalTime": data.totalTime || "PT30M",
          "estimatedCost": {
            "@type": "MonetaryAmount",
            "currency": "EUR",
            "value": data.cost || "0"
          },
          "step": data.steps?.map((step, idx) => ({
            "@type": "HowToStep",
            "position": idx + 1,
            "name": step.name,
            "text": step.text,
            "image": step.image
          })) || []
        };

      case 'Review':
        return {
          "@context": "https://schema.org",
          "@type": "Review",
          "itemReviewed": {
            "@type": "LocalBusiness",
            "name": "Arreglatelo",
            "image": "https://rma.arreglatelo.es/logo.png"
          },
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": data.rating,
            "bestRating": "5"
          },
          "author": {
            "@type": "Person",
            "name": data.author
          },
          "reviewBody": data.text,
          "datePublished": data.date
        };

      case 'BreadcrumbList':
        return {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": data.items?.map((item, idx) => ({
            "@type": "ListItem",
            "position": idx + 1,
            "name": item.name,
            "item": item.url
          })) || []
        };

      case 'WebPage':
        return {
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": data.title,
          "description": data.description,
          "url": data.url,
          "isPartOf": {
            "@type": "WebSite",
            "name": "Arreglatelo",
            "url": "https://rma.arreglatelo.es"
          },
          "inLanguage": data.language || "es",
          "datePublished": data.datePublished,
          "dateModified": data.dateModified
        };

      case 'RepairService':
        return {
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "Repair Service",
          "name": `Reparación ${data.deviceName} en ${data.cityName}`,
          "description": data.description,
          "provider": baseOrganization,
          "areaServed": {
            "@type": "City",
            "name": data.cityName
          },
          "offers": {
            "@type": "Offer",
            "priceSpecification": {
              "@type": "PriceSpecification",
              "priceCurrency": "EUR",
              "minPrice": data.minPrice,
              "maxPrice": data.maxPrice
            },
            "availability": "https://schema.org/InStock",
            "validFrom": new Date().toISOString()
          },
          "termsOfService": "Garantía de 6 meses incluida",
          "serviceOutput": {
            "@type": "Product",
            "name": `${data.deviceName} reparado`
          }
        };

      default:
        return null;
    }
  };

  const schema = generateSchema();
  
  if (!schema) return null;

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Multiple schemas for a single page
export const MultiSchemaMarkup = ({ schemas }) => {
  return (
    <Helmet>
      {schemas.map((schema, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

// Canonical and Hreflang tags component
export const SEOHead = ({ 
  title, 
  description, 
  canonical, 
  image,
  type = 'website',
  noindex = false,
  languages = ['es', 'en', 'fr', 'it', 'de']
}) => {
  const baseUrl = 'https://rma.arreglatelo.es';
  const currentPath = canonical || window.location.pathname;
  const fullUrl = `${baseUrl}${currentPath}`;

  return (
    <Helmet>
      {/* Basic Meta */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Canonical */}
      <link rel="canonical" href={fullUrl} />
      
      {/* Hreflang Tags */}
      {languages.map(lang => (
        <link 
          key={lang}
          rel="alternate" 
          hrefLang={lang} 
          href={`${baseUrl}/${lang}${currentPath}`} 
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={fullUrl} />
      
      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Arreglatelo" />
      {image && <meta property="og:image" content={image} />}
      <meta property="og:locale" content="es_ES" />
      <meta property="og:locale:alternate" content="en_GB" />
      <meta property="og:locale:alternate" content="fr_FR" />
      <meta property="og:locale:alternate" content="it_IT" />
      <meta property="og:locale:alternate" content="de_DE" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {image && <meta name="twitter:image" content={image} />}
    </Helmet>
  );
};

export default SchemaMarkup;
